import { useId, useState } from 'react'
import { appDay } from 'shared/day'
import { type DateKey, durationParser } from 'shared/search-adapters'
import { ListSelect } from 'shared/ui'

import { ReactDatePicker } from '../../../../components/atoms/ReactDatePicker/ReactDatePicker'
import { type DateOption, DATES } from './const'
import css from './DatePickerRange.module.css'
import form from './Filters.module.css'

const enum Index {
	End = 1,
	Start = 0
}

const isCustom = (option: DateOption | null): option is DateOption =>
	option?.label === DATES.at(-1)?.label

const parseInitialDateOptions = (current: DateOption | null) => {
	if (!current) {
		return [...DATES]
	}

	const index = DATES.findIndex(option => option.label === current.label)

	if (index === -1) {
		return [...DATES]
	}

	return DATES.with(index, current)
}

function parseInitialDates(key: DateKey): DateOption | null {
	const params = new URLSearchParams(location.search)

	const dates = durationParser.parse(params, key)

	if (!dates?.length) {
		return null
	}

	const start = dates[0]

	const withOutCustom = DATES.slice(0, 2)
	const found = withOutCustom.find(option =>
		option.value?.[Index.Start].isSame(start)
	)
	if (found) {
		return found
	}

	return {
		label: 'Custom',
		value: dates
	}
}

const useDates = (key: DateKey) => useState(() => parseInitialDates(key))

type DateState = ReturnType<typeof useDates>

const DatePickerRange = ({
	endDate,
	onChangeEndDate,
	onChangeStartDate,
	startDate
}: {
	endDate?: Date
	onChangeEndDate: (date: Date) => void
	onChangeStartDate: (date: Date) => void
	startDate?: Date
}) => (
	<div className={css.root}>
		<ReactDatePicker
			endDate={endDate}
			maxDate={endDate}
			onChange={date => date instanceof Date && onChangeStartDate(date)}
			placeholder='From'
			selected={startDate}
			selectsStart
			startDate={startDate}
		/>
		<ReactDatePicker
			maxDate={appDay().toDate()}
			minDate={startDate}
			onChange={date => date instanceof Date && onChangeEndDate(date)}
			placeholder='To'
			popperPlacement='bottom-end'
			selected={endDate}
			selectsEnd
			startDate={startDate}
		/>
	</div>
)

Dates.useDates = useDates
export function Dates({
	dates,
	label,
	placeholder,
	setDates
}: {
	dates: DateState[0]
	label: string
	placeholder: string
	setDates: DateState[1]
}) {
	const id = useId()
	const [options, setOptions] = useState(() => parseInitialDateOptions(dates))

	const setCustom = (date: Date, index: number) =>
		setOptions(previous => {
			const last = previous.at(-1)
			last!.value[index] = appDay(date)
			setDates(last!)
			return previous.with(-1, last!)
		})

	return (
		<>
			<div className={form.set}>
				<label className={form.label} htmlFor={id}>
					{label}
				</label>
				<ListSelect
					inputId={id}
					isClearable
					onChange={value => setDates(value)}
					options={options}
					placeholder={placeholder}
					value={dates}
				/>
			</div>
			{isCustom(dates) && (
				<DatePickerRange
					endDate={dates.value[1].toDate()}
					onChangeEndDate={date => setCustom(date, Index.End)}
					onChangeStartDate={date => setCustom(date, Index.Start)}
					startDate={dates.value[0].toDate()}
				/>
			)}
		</>
	)
}
